import "./ForecastingPage.scss";
import Papa from "papaparse";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ForecastGroup,
  GetForecastsParams,
  GetForecastsResponse
} from "@blisspointmedia/bpm-types/dist/Forecasting";
import { RouteComponentProps, Router, useLocation } from "@reach/router";
import {
  Button,
  ButtonType,
  Dropdown,
  DropdownToggleType,
  Page,
  TextToggleButton
} from "../Components";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import * as UserRedux from "../redux/user";
import { dateToMddyy } from "../utils/date-utils";
import { awaitJSON, MetricsLambdaFetch, pollS3 } from "../utils/fetch-utils";
import { useMap } from "../utils/hooks/useData";
import { useTabbedNav } from "../utils/hooks/useNav";
import BudgetBuilder from "./BudgetBuilder/BudgetBuilder";
import {
  BestModelRow,
  BudgetViewOptions,
  ECON_BUCKET,
  FoldDataRow,
  ForecastPropertyValueType,
  FullForecastSelection,
  MatchableForecastData,
  MatchableForecastOptions,
  ModelSummaryData,
  NAVS,
  PageTab,
  SelectableForecastProperty
} from "./forecastingConstants";
import {
  buildFullForecastSelection,
  extractForecastValsFromQueryParams,
  getCombinedChannelPlatform,
  getCombinedDateRange,
  getS3Filename,
  getSelectablePropertyFromForecast
} from "./forecastingUtils";
import ForecastResultsPage from "./ForecastResults/ForecastResultsPage";
import SavedForecasts from "./SavedForecasts/SavedForecasts";

const ForecastingPage = React.memo(({ navigate }: RouteComponentProps) => {
  const location = useLocation();
  const [forecastGroups, setForecastGroups] = useState<ForecastGroup[] | undefined>();
  const [foldDataMap, setFoldDataValue] = useMap<string, FoldDataRow[]>({});
  const [bestModelDataMap, setBestModelDataValue] = useMap<string, BestModelRow[]>();
  const [modelSummaryDataMap, setModelSummaryDataValue] = useMap<string, ModelSummaryData>();
  const [budgetView, setBudgetView] = useState<BudgetViewOptions>("showPercentage");
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const isAdmin = useSelector(UserRedux.isAdminSelector);
  const { tab, goToTab } = useTabbedNav({
    navigate,
    baseURL: "forecasting/",
    defaultKey: PageTab.FORECAST_RESULTS,
    navs: NAVS.map(nav => nav.key),
  });
  const showWIPFeatures = false;

  const tabPathSegment = useMemo(() => {
    return tab.split("/")[0];
  }, [tab]);

  const { forecastId, kpiName, outcomeVar } = useMemo(() => {
    return extractForecastValsFromQueryParams(location.search);
  }, [location.search]);

  // Fetch forecasts associated with company
  useEffect(() => {
    const fetchForecastGroups = async () => {
      const params: GetForecastsParams = { company: cid };

      let res = await MetricsLambdaFetch("/getForecasts", {
        params,
      });
      const resJSON = await awaitJSON<GetForecastsResponse>(res);
      const { forecastGroups } = resJSON;
      setForecastGroups(forecastGroups);
    };

    if (!forecastGroups) {
      fetchForecastGroups();
    }
  }, [forecastGroups, cid]);

  // If no forecast/kpi selected, select a default run
  useEffect(() => {
    if (
      [PageTab.FORECAST_RESULTS, PageTab.BUDGET_BUILDER].includes(tabPathSegment) &&
      forecastGroups &&
      !forecastId
    ) {
      if (forecastGroups.length < 1) {
        // [DEV-18749] TODO: Pretty empty state that lets people request their first forecast
        setError({ message: `No forecasts found for ${cid}` });
        return;
      }

      // [DEV-18750] TODO: Default to the newest model with the first alphabetical channel/platform
      const firstForecastId = forecastGroups[0].forecasts[0].id;
      const firstKpi = forecastGroups[0].kpis[0];

      if (navigate) {
        navigate(
          `${tabPathSegment}?forecastId=${firstForecastId}&kpiName=${firstKpi.name}&outcomeVar=${firstKpi.type}`
        );
      }
    }
  }, [cid, tabPathSegment, forecastGroups, forecastId, navigate, setError]);

  const fullSelectedForecast: FullForecastSelection | undefined = useMemo(() => {
    if (!forecastId || !forecastGroups || !kpiName || !outcomeVar) {
      return;
    }

    const forecastGroup = forecastGroups.find(group => {
      return group.forecasts.find(forecast => forecast.id === forecastId);
    });

    if (!forecastGroup) {
      return;
    }

    const forecast = forecastGroup.forecasts.find(forecast => forecast.id === forecastId);
    if (!forecast) {
      return;
    }

    const kpi = forecastGroup.kpis.find(kpi => kpi.name === kpiName && kpi.type === outcomeVar);
    if (!kpi) {
      return;
    }

    return buildFullForecastSelection(forecastGroup, forecast, kpi);
  }, [forecastGroups, forecastId, kpiName, outcomeVar]);

  const selectedFoldData = useMemo(() => {
    return fullSelectedForecast ? foldDataMap[fullSelectedForecast?.id] : undefined;
  }, [foldDataMap, fullSelectedForecast]);

  // Grab _fold_data file from S3 for selected forecast
  const fetchFoldData = useCallback(
    async (forecastId: string) => {
      if (!fullSelectedForecast) {
        return;
      }

      const { channel, platform } = fullSelectedForecast;

      try {
        const filename = getS3Filename({
          cid,
          channel,
          platform,
          forecastId,
          suffix: "fold_data",
        });
        let foldDataRes = await pollS3({
          bucket: ECON_BUCKET,
          mimeType: "application/json",
          filename,
          autoDownload: false,
          noTimeout: true,
          maxAttempts: 3,
        });

        const foldDataText = await foldDataRes.text();

        const { data: foldData }: { data: FoldDataRow[] } = Papa.parse(foldDataText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
        });

        setFoldDataValue(forecastId, foldData);
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to fetch fold data: ${reportError.message}`,
          reportError,
        });
      }
    },
    [cid, fullSelectedForecast, setError, setFoldDataValue]
  );

  useEffect(() => {
    if (fullSelectedForecast && !selectedFoldData) {
      fetchFoldData(fullSelectedForecast.id);
    }
  }, [fetchFoldData, fullSelectedForecast, selectedFoldData]);

  const selectedBestModelData = useMemo(() => {
    return fullSelectedForecast ? bestModelDataMap[fullSelectedForecast?.id] : undefined;
  }, [bestModelDataMap, fullSelectedForecast]);

  // Grab _best_model file from S3 for selected forecast
  const fetchBestModelData = useCallback(
    async (forecastId: string) => {
      if (!fullSelectedForecast) {
        return;
      }

      try {
        const { channel, platform } = fullSelectedForecast;

        const filename = getS3Filename({
          cid,
          channel,
          platform,
          forecastId,
          suffix: "allbudgets_bestmodel",
        });
        let bestModelDataRes = await pollS3({
          bucket: ECON_BUCKET,
          mimeType: "application/json",
          filename,
          autoDownload: false,
          noTimeout: true,
          maxAttempts: 3,
        });

        const bestModelDataText = await bestModelDataRes.text();
        const { data: bestModelData }: { data: BestModelRow[] } = Papa.parse(bestModelDataText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
        });

        setBestModelDataValue(forecastId, bestModelData);
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to fetch best model data: ${reportError.message}`,
          reportError,
        });
      }
    },
    [cid, fullSelectedForecast, setBestModelDataValue, setError]
  );

  useEffect(() => {
    if (fullSelectedForecast && !selectedBestModelData) {
      fetchBestModelData(fullSelectedForecast.id);
    }
  }, [fetchBestModelData, fullSelectedForecast, selectedBestModelData]);

  const selectedModelSummaryData = useMemo(() => {
    return fullSelectedForecast ? modelSummaryDataMap[fullSelectedForecast?.id] : undefined;
  }, [fullSelectedForecast, modelSummaryDataMap]);

  // Grab _model_summary file from S3 for selected forecast
  const fetchModelSummaryData = useCallback(
    async (forecastId: string) => {
      if (!fullSelectedForecast) {
        return;
      }

      try {
        const { channel, platform } = fullSelectedForecast;

        const filename = getS3Filename({
          cid,
          channel,
          platform,
          forecastId,
          suffix: "model_summary",
        });
        let modelSummaryRes = await pollS3({
          bucket: ECON_BUCKET,
          mimeType: "application/json",
          filename,
          autoDownload: false,
          noTimeout: true,
          maxAttempts: 3,
        });

        const modelSummaryText = await modelSummaryRes.text();

        const { data: modelSummaryData }: { data: ModelSummaryData[] } = Papa.parse(
          modelSummaryText,
          {
            header: true,
            skipEmptyLines: true,
            dynamicTyping: true,
          }
        );

        setModelSummaryDataValue(forecastId, modelSummaryData[0]);
      } catch (e) {
        const reportError = e as Error;
        setError({
          message: `Failed to fetch model summary data: ${reportError.message}`,
          reportError,
        });
      }
    },
    [cid, fullSelectedForecast, setError, setModelSummaryDataValue]
  );

  useEffect(() => {
    if (fullSelectedForecast && !selectedModelSummaryData) {
      fetchModelSummaryData(fullSelectedForecast.id);
    }
  }, [fetchModelSummaryData, fullSelectedForecast, selectedModelSummaryData]);

  const selectedForecastProperties: MatchableForecastData | undefined = useMemo(() => {
    if (!fullSelectedForecast || !forecastId || !outcomeVar || !kpiName) {
      return undefined;
    }

    const { channel, platform, primaryBudget, startDate, endDate } = fullSelectedForecast;

    return {
      forecastId: forecastId,
      [SelectableForecastProperty.CHANNEL_PLATFORM]: getCombinedChannelPlatform(channel, platform),
      [SelectableForecastProperty.OUTCOME_VARIABLE]: outcomeVar,
      [SelectableForecastProperty.KPI]: kpiName,
      [SelectableForecastProperty.BUDGET]: primaryBudget,
      [SelectableForecastProperty.DATE_RANGE]: getCombinedDateRange(startDate, endDate),
    };
  }, [forecastId, fullSelectedForecast, kpiName, outcomeVar]);

  // List of forecasts which could potentially be selected from the subheader dropdowns,
  // Because all but one of their selectable properties match
  const forecastsMatchingAllButOne: MatchableForecastData[] = useMemo(() => {
    if (
      !forecastGroups ||
      !fullSelectedForecast ||
      !selectedForecastProperties ||
      !forecastId ||
      !outcomeVar ||
      !kpiName
    ) {
      return [];
    }

    const matchableForecasts: MatchableForecastData[] = forecastGroups.flatMap(group => {
      const { startDate, endDate } = group;
      return group.forecasts.flatMap(forecast => {
        const { channel, platform, primaryBudget } = forecast;
        return group.kpis.flatMap(kpi => {
          const { type, name } = kpi;
          return {
            forecastId: forecast.id,
            [SelectableForecastProperty.CHANNEL_PLATFORM]: getCombinedChannelPlatform(
              channel,
              platform
            ),
            [SelectableForecastProperty.OUTCOME_VARIABLE]: type,
            [SelectableForecastProperty.KPI]: name,
            [SelectableForecastProperty.BUDGET]: primaryBudget,
            [SelectableForecastProperty.DATE_RANGE]: getCombinedDateRange(startDate, endDate),
          };
        });
      });
    });

    const matchableForecastsByDiff: Record<string, MatchableForecastData[]> = {};

    matchableForecasts.forEach(potentialMatch => {
      let numDiff = 0;

      Object.values(SelectableForecastProperty).forEach(propKey => {
        if (potentialMatch[propKey] !== selectedForecastProperties[propKey]) {
          numDiff++;
        }
      });

      if (!matchableForecastsByDiff[String(numDiff)]) {
        matchableForecastsByDiff[String(numDiff)] = [];
      }

      matchableForecastsByDiff[String(numDiff)].push(potentialMatch);
    });

    return matchableForecastsByDiff["1"] ?? [];
  }, [
    forecastGroups,
    forecastId,
    fullSelectedForecast,
    kpiName,
    outcomeVar,
    selectedForecastProperties,
  ]);

  const {
    [SelectableForecastProperty.CHANNEL_PLATFORM]: channelPlatformSelectableOptions,
    [SelectableForecastProperty.OUTCOME_VARIABLE]: outcomeVariableSelectableOptions,
    [SelectableForecastProperty.KPI]: kpiSelectableOptions,
    [SelectableForecastProperty.BUDGET]: budgetSelectableOptions,
    [SelectableForecastProperty.DATE_RANGE]: dateRangeSelectableOptions,
  }: MatchableForecastOptions = useMemo(() => {
    const output = {};

    Object.values(SelectableForecastProperty).forEach(propKey => {
      const propSet = new Set(forecastsMatchingAllButOne.map(match => match[propKey]));
      if (selectedForecastProperties) {
        propSet.delete(selectedForecastProperties[propKey]);
      }
      output[propKey] = Array.from(propSet);
    });

    return output as MatchableForecastOptions;
  }, [forecastsMatchingAllButOne, selectedForecastProperties]);

  const selectForecastWithNewProperty = useCallback(
    <P extends SelectableForecastProperty>(
      propertyName: P,
      value: ForecastPropertyValueType<P>
    ) => {
      if (!selectedForecastProperties) {
        console.error("selectedForecastProperties undefined in selectForecastWithNewProperty");
        return;
      }

      const foundMatch = forecastsMatchingAllButOne.find(potentialMatch => {
        return Object.values(SelectableForecastProperty).reduce((isMatch, propKey) => {
          if (propKey === propertyName) {
            return potentialMatch[propKey] === value && isMatch;
          }

          return potentialMatch[propKey] === selectedForecastProperties[propKey] && isMatch;
        }, true);
      });

      if (foundMatch) {
        if (navigate) {
          navigate(
            `${tabPathSegment}?forecastId=${foundMatch.forecastId}&kpiName=${
              foundMatch[SelectableForecastProperty.KPI]
            }&outcomeVar=${foundMatch[SelectableForecastProperty.OUTCOME_VARIABLE]}`
          );
        }
      } else {
        setError({
          message: `Unable to locate a matching forecast with ${propertyName} = ${value}`,
        });
      }
    },
    [forecastsMatchingAllButOne, navigate, selectedForecastProperties, setError, tabPathSegment]
  );

  return (
    <Page
      app2Redesign
      title="Forecasting"
      pageType="Forecasting"
      navs={NAVS}
      selectedNav={tabPathSegment}
      onNav={goToTab}
      actions={
        tabPathSegment === PageTab.FORECAST_RESULTS ? (
          <div className="forecastingActions">
            {dateRangeSelectableOptions.length > 0 ? (
              <Dropdown
                type={DropdownToggleType.FILLED}
                background="dark"
                label="Forecast dates"
                value={
                  fullSelectedForecast
                    ? getCombinedDateRange(
                        fullSelectedForecast.startDate,
                        fullSelectedForecast.endDate
                      )
                    : undefined
                }
                options={dateRangeSelectableOptions}
                onChange={option =>
                  selectForecastWithNewProperty(SelectableForecastProperty.DATE_RANGE, option)
                }
              />
            ) : (
              <Button
                type={ButtonType.EMPTY}
                background="dark"
                className="forecastingDropdownWithoutOptions"
              >
                Forecast dates:{" "}
                {fullSelectedForecast
                  ? `${dateToMddyy(fullSelectedForecast.startDate)} – ${dateToMddyy(
                      fullSelectedForecast.endDate
                    )}`
                  : undefined}
              </Button>
            )}
          </div>
        ) : undefined
      }
      subHeader={
        [PageTab.FORECAST_RESULTS, PageTab.BUDGET_BUILDER].includes(tabPathSegment) && (
          <div className="forecastingSubheader">
            {tabPathSegment === PageTab.FORECAST_RESULTS && (
              <>
                {channelPlatformSelectableOptions.length > 0 ? (
                  <Dropdown
                    type={DropdownToggleType.FILLED}
                    label="Channel"
                    value={
                      fullSelectedForecast
                        ? getCombinedChannelPlatform(
                            fullSelectedForecast.channel,
                            fullSelectedForecast.platform
                          )
                        : undefined
                    }
                    options={channelPlatformSelectableOptions}
                    onChange={option =>
                      selectForecastWithNewProperty(
                        SelectableForecastProperty.CHANNEL_PLATFORM,
                        option
                      )
                    }
                  />
                ) : (
                  <Button type={ButtonType.EMPTY} className="forecastingDropdownWithoutOptions">
                    Channel:{" "}
                    {fullSelectedForecast
                      ? getSelectablePropertyFromForecast(
                          fullSelectedForecast,
                          SelectableForecastProperty.CHANNEL_PLATFORM
                        )
                      : undefined}
                  </Button>
                )}
              </>
            )}
            {outcomeVariableSelectableOptions.length > 0 ? (
              <Dropdown
                type={DropdownToggleType.FILLED}
                label="Outcome Variable"
                value={outcomeVar}
                options={outcomeVariableSelectableOptions}
                onChange={option =>
                  selectForecastWithNewProperty(SelectableForecastProperty.OUTCOME_VARIABLE, option)
                }
              />
            ) : (
              <Button type={ButtonType.EMPTY} className="forecastingDropdownWithoutOptions">
                Outcome Variable: {outcomeVar}
              </Button>
            )}
            {kpiSelectableOptions.length > 0 ? (
              <Dropdown
                type={DropdownToggleType.FILLED}
                label="KPI"
                value={kpiName}
                options={kpiSelectableOptions}
                onChange={option =>
                  selectForecastWithNewProperty(SelectableForecastProperty.KPI, option)
                }
              />
            ) : (
              <Button type={ButtonType.EMPTY} className="forecastingDropdownWithoutOptions">
                KPI: {kpiName}
              </Button>
            )}
            {showWIPFeatures && (
              <TextToggleButton
                className="forecastsBudgetDisplayToggle"
                design="secondary-light"
                options={[
                  { key: "showPercentage", label: "Show % Budgets" },
                  { key: "showDollars", label: "Show $ Budgets" },
                ]}
                selectedOption={budgetView}
                onChange={option => setBudgetView(option)}
              />
            )}
          </div>
        )
      }
    >
      <Router className="fullPageRouter forecastingPage">
        <ForecastResultsPage
          default
          path={PageTab.FORECAST_RESULTS}
          selectedForecast={fullSelectedForecast}
          foldData={selectedFoldData}
          modelSummaryData={selectedModelSummaryData}
          bestModelData={selectedBestModelData}
          budgetSelectableOptions={budgetSelectableOptions}
          selectNewBudget={budget =>
            selectForecastWithNewProperty(SelectableForecastProperty.BUDGET, budget)
          }
          cid={cid}
        />
        <SavedForecasts
          path={PageTab.SAVED_FORECASTS}
          forecastGroups={forecastGroups}
          budgetView={budgetView}
          setBudgetView={setBudgetView}
          setError={setError}
          isAdmin={isAdmin}
          showWIPFeatures={showWIPFeatures}
        />
        <BudgetBuilder path={PageTab.BUDGET_BUILDER} />
      </Router>
    </Page>
  );
});

export default ForecastingPage;
